import { Messages } from '../enums/messages';
import { HttpHelper } from '../helper/http';
import type { RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
import store from '../store';
import router from '../router';
import type { AppConfiguration } from '../types/appConfiguration';
import { REGEX_USER_EDIT_PATH } from '../constants/regex';

/**
 * This pushes meta content into the header before navigation change and helps
 * with pre-rendering and SEO.
 *
 * @param {object} to The router navigation record for the next route.
 */
export function MetaGuard(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): any {
	// This goes through the matched routes from last to first, finding the closest route with a title.
	// eg. if we have /some/deep/nested/route and /some, /deep, and /nested have titles, nested's will be chosen.
	const nearestWithTitle = to.matched.toReversed().find(r => r.meta && r.meta.title);

	// Find the nearest route element with meta tags.
	const nearestWithMeta: any = to.matched.toReversed().find(r => r.meta && r.meta.metaTags);

	// If a route with a title was found, set the document (page) title to that value.
	if (nearestWithTitle) (document.title as any) = nearestWithTitle.meta.title;

	// Remove any stale meta tags from the document using the key attribute we set below.
	Array.from(document.querySelectorAll('[data-vue-router-controlled]'), (el: Element) => el.parentNode?.removeChild(el));

	// Skip rendering meta tags if there are none.
	if (!nearestWithMeta) return next();

	// Turn the meta tag definitions into actual elements in the head.
	nearestWithMeta.meta.metaTags.map((tagDef: any) => {
		const tag = document.createElement('meta');

		Object.keys(tagDef).forEach(key => {
			tag.setAttribute(key, tagDef[key]);
		});

		// We use this to track which meta tags we create, so we don't interfere with other ones.
		tag.setAttribute('data-vue-router-controlled', '');
		return tag;

	// Add the meta tags to the document head.
	}).forEach((tag: Element) => document.head.appendChild(tag));

	return next();
}

const LICENSING_RULES = new Map<string, (config: AppConfiguration) => boolean>([
	['/licensing/user-licensing', config => config.licenceModel === 'User Sessions'],
	['/licensing/login-usage', config => config.licenceModel === 'User Sessions'],
	['/licensing/ai-unit-usage', config => config.isAiUnitActive === true],
	['/licensing/idp-usage', config => config.isIdpActive === true],
]);

function isLicensingRoute(path: string): boolean {
	return LICENSING_RULES.has(path);
}

function canAccessLicensing(path: string, config: AppConfiguration): boolean {
	const rule = LICENSING_RULES.get(path);
	if (!rule) return true;
	return rule(config);
}

const ADMIN_ONLY_PATHS: string[] = ['/users/create'];

function isAdminOnlyRoute(path: string): boolean {
	if (ADMIN_ONLY_PATHS.includes(path)) return true;

	if (REGEX_USER_EDIT_PATH.test(path)) return true;

	return false;
}

/**
 * This function verifies that the user has a valid account after login, and will kick the user
 * to the login page if the account loaded is invalid.
 *
 * @param {object} to The router navigation record for the next route.
 */
export async function AuthGuard(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): Promise<any> {
	// Check public access.
	const isPublic: boolean = (to.meta.public as any) || false;
	const isLoggedInMeta: boolean = (to.meta.loggedIn as any) || false;
	const isRpa: boolean = (to.meta.rpa as any) || false;
	const isCreate: boolean = (to.meta.create as any) || false;
	const isAi: boolean = (to.meta.ai as any) || false;
	const isAdmin: boolean = (to.meta.admin as any) || false;
	const isConverse: boolean = (to.meta.converse as any) || false;
	const isSpark: boolean = (to.meta.spark as any) || false;
	const isIdp: boolean = (to.meta.idp as any) || false;
	const isSingleTenant: boolean = (to.meta.singleTenant as any) || true;
	const isReporting: boolean = (to.meta.reporting as any) || false;

	// Always persist `nosso=1` when present, even for public routes.
	const no_sso = to.query?.nosso === '1';
	if (no_sso) {
		window.localStorage.setItem('lc:nosso', '1');
	}

	// If public, then allow through and return.
	if (isPublic) return next();

	// Get the account information.
	const account = (store.state as any).account;

	// Check for not logged in user. Record requested route path and redirect to login page.
	if (!account.isLoggedIn) {
		const query = new URLSearchParams(to.query as any).toString();
		const query_string = query.length > 0 ? `?${query}` : '';

		window.localStorage.setItem('lc:login_destination', `${to.path}${query_string}` || '/');
		return next({ path: '/auth/login' });
	}

	// Load configuration once if needed.
	if (store.state.configuration_loaded === false) {
		const http = new HttpHelper(store, router);
		const response = await http.get('/api/configuration', { autoRefresh: 'auto' });
		if (response.status === 200) {
			const result = await response.json();
			store.dispatch('setConfiguration', result);
		} else if (response.status !== 401) {
			return next({ path: '/not-found' });
		}
	}

	// --- DEV ONLY ---
	if (to.meta.dev) {
		if (!account.isLoggedIn || !account.isAdmin || !document.cookie.includes('magicdev=yes')) {
			return next({ path: '/not-found' });
		}
	}

	if (isLoggedInMeta && account.email && to.path === '/') {
		return next();
	}

	// Check for RPA access.
	if (isRpa && store.state.configuration.isRpaActive && account.isLoggedIn && (account.isRpaUser || account.isAdmin)) {
		return next();
	}

	// Check for Create access.
	if (isCreate && account.isLoggedIn && (account.isCreateUser || account.isAdmin)) {
		return next();
	}

	// Check for AI Agents list access.
	if (
		(to.path === '/ai/agents' || to.path === '/ai/agents/')
		&& account.isLoggedIn
		&& (account.isAiUser || account.isAdmin)
	) {
		return next();
	}

	// Check for AI Agent access.
	if (
		(to.path === '/ai/agent' || to.path.startsWith('/ai/agent/'))
		&& account.isLoggedIn
		&& (account.isAiUser || account.isAdmin)
	) {
		if (store.state.configuration.isAgentManagementActive) {
			return next();
		}

		store.dispatch('notification/add', {
			type: 'warning',
			message: Messages.USER_ACCESS_DENIED,
		});
		return next({ path: '/' });
	}

	// Check for AI Models access.
	if (isAi && account.isLoggedIn && (account.isAiUser || account.isAdmin) && to.path.startsWith('/ai/model')) {
		return next();
	}

	// Check for AI Knowledge Management access.
	if (
		isAi
		&& account.isLoggedIn
		&& (account.isAiUser || account.isAdmin)
		&& ['/ai/knowledge-management/local/collections', '/ai/knowledge-management/dynamic/groups'].includes(to.path)
	) {
		return next();
	}

	// Check for AI access.
	if (isAi && store.state.configuration.isAiActive && account.isLoggedIn && (account.isAiUser || account.isAdmin)) {
		return next();
	}

	// Check for Converse CX access.
	if (
		isConverse
		&& store.state.configuration.isConverseActive
		&& store.state.configuration.isConverseAccountCreated
		&& account.isLoggedIn
		&& (account.isAdmin || account.isConverseCxUser)
	) {
		return next();
	}

	// Check for Spark access.
	if (
		isSpark
		&& store.state.configuration.isSparkActive
		&& store.state.configuration.isSparkAccountCreated
		&& account.isLoggedIn
		&& (account.isAdmin || account.isSparkUser)
	) {
		return next();
	}

	// Check for IDP access.
	if (isIdp && store.state.configuration.isIdpActive && account.isLoggedIn && (account.isAdmin || account.isIdpUser)) {
		return next();
	}

	// Check for multi-tenant access.
	if (isSingleTenant && !account.isMultiTenant && account.isAdmin) {
		if (to.path === '/reports') {
			return next();
		}
		if (to.path === '/settings/intrusion_prevention') {
			return next();
		}
	}

	// --- ADMIN / REPORTING SECTION ---
	if ((isAdmin || isReporting) && account.isLoggedIn && (account.isReportingUser || account.isAdmin)) {
		const path = to.path;

		// 1) LICENSING – /not-found
		if (isLicensingRoute(path)) {
			const config = store.state.configuration as AppConfiguration;

			if (!canAccessLicensing(path, config)) {
				return next({ path: '/not-found' });
			}

			return next();
		}

		// 2) ADMIN-ONLY ROUTES - /users/create, /users/edit/:user_id
		if (isAdminOnlyRoute(path) && !account.isAdmin) {
			store.dispatch('notification/add', {
				type: 'warning',
				message: Messages.USER_ACCESS_DENIED,
			});

			const redirectPath = account.isReportingUser ? '/users' : '/';
			return next({ path: redirectPath });
		}

		return next();
	}

	// Otherwise send to home page with a message.
	store.dispatch('notification/add', {
		type: 'warning',
		message: Messages.USER_ACCESS_DENIED,
	});
	return next({ path: '/' });
}
